import qs from 'query-string'
import Cookie from 'lib/cookie'
import isNil from 'lodash/isNil'
import omitBy from 'lodash/omitBy'

import { LOAD_NUMBER } from 'config'
import { axiosBaseQuery } from 'store/libs/baseQuery'
import { yayApi } from '../baseApi'
import { getDeviceId } from 'lib/user'
import {
  pathApiGroupsEdit,
  pathApiGroupsFire,
  pathApiGroupsJoinedStatuses,
  pathApiGroupsNew,
  pathApiGroupsShow,
  pathApiGroupsShowRequestWalkthrough,
  pathApiGroupsVisit,
} from 'lib/paths'
import {
  GroupMember,
  Group,
  User,
  PaginationResult,
  GroupCategory,
  Post,
  GroupRelationship,
  GroupCreate,
} from 'lib/_types'

export const groupApi = yayApi
  .enhanceEndpoints({
    addTagTypes: [
      'GroupCategories',
      'Groups/Mine',
      'Groups/Mine/Pinned',
      'UserGroups',
      'Groups',
      'Group/Base',
      'Group/Members',
      'Group/Relatable',
      'Group/Related',
      'Group/JoinedStatuses',
      'Activities',
    ],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getGroupCategories: build.query<Array<GroupCategory>, {}>({
        providesTags: ['GroupCategories'],
        query: () => ({
          url: `/v1/groups/categories`,
          params: {
            number: 100,
          },
        }),
        transformResponse: (response) => response?.group_categories ?? [],
      }),

      getMine: build.query<
        Array<Group>,
        {
          fromTimestamp?: string
          isSidebar?: boolean
        }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'Groups/Mine', id: args?.fromTimestamp }]
        },
        query: ({ fromTimestamp }) => {
          return {
            url: '/v2/groups/mine',
            params: {
              number: LOAD_NUMBER,
              ...(fromTimestamp && fromTimestamp != '0'
                ? {
                    from_timestamp: fromTimestamp,
                  }
                : {}),
            },
          }
        },
        transformResponse: (response) =>
          response?.groups?.map?.((x: Group) => {
            return {
              ...x,
              is_joined: true, // BE doesn't provide this.
            }
          }) ?? [],
        keepUnusedDataFor: 0,
      }),

      getMinePinned: build.query<Array<Group>, {}>({
        providesTags: ['Groups/Mine/Pinned'],
        query: ({}) => {
          return {
            url: '/v2/groups/mine',
            params: {
              number: LOAD_NUMBER,
            },
          }
        },
        transformResponse: (response) =>
          response?.pinned_groups?.map((x: Group) => ({
            ...x,
            _pinned: true,
            is_joined: true, // BE doesn't provide this.
          })) ?? [],
        keepUnusedDataFor: 0,
      }),

      getUserGroupList: build.query<
        Array<Group>,
        { userId: string; page: string }
      >({
        providesTags: (_0, _1, args) => {
          return [{ type: 'UserGroups', id: args?.page }]
        },
        query: ({ userId, page }) => {
          return {
            url: '/v1/groups/user_group_list',
            params: {
              number: LOAD_NUMBER,
              user_id: userId,
              page,
            },
          }
        },
        transformResponse: (response) => response?.groups || [],
      }),

      getGroups: build.query<
        Array<Group>,
        {
          groupCategoryId?: number
          keyword?: string
          subCategoryId?: string
          fromTimestamp?: string
          createdAwhile?: boolean
          showing?: boolean
          hasCoverImage?: boolean
          number?: number
        }
      >({
        providesTags: ['Groups'],
        query: ({
          groupCategoryId,
          keyword,
          subCategoryId,
          fromTimestamp,
          createdAwhile,
          showing,
          hasCoverImage,
          number = LOAD_NUMBER,
        }) => ({
          url: '/v2/groups',
          params: {
            number,
            ...omitBy(
              {
                group_category_id: groupCategoryId,
                keyword,
                created_awhile: createdAwhile,
                showing: showing,
                sub_category_id: subCategoryId,
                has_cover_image: hasCoverImage,
              },
              isNil
            ),
            ...(fromTimestamp && fromTimestamp != '0'
              ? {
                  from_timestamp: fromTimestamp,
                }
              : {}),
          },
        }),
        transformResponse: (response) => response?.groups || [],
        keepUnusedDataFor: 0,
      }),

      getGroup: build.query<Group, { groupId: string }>({
        providesTags: ['Group/Base'],
        query: ({ groupId }) => ({
          url: pathApiGroupsShow({ groupId }),
        }),
        transformResponse: (response) => response?.group || {},
      }),

      getGroupMutate: build.mutation<Group, { groupId: string }>({
        query: ({ groupId }) => ({
          url: pathApiGroupsShow({ groupId }),
        }),
      }),

      visitGroup: build.mutation<Group, { groupId: string }>({
        query: ({ groupId }) => ({
          url: pathApiGroupsVisit({ groupId }),
          method: 'POST',
        }),
      }),

      getMembers: build.query<
        Array<GroupMember>,
        {
          groupId: string
          fromId?: number | null
          keyword?: string
          mode?:
            | 'moderator'
            | 'pending'
            | 'pending_deputize'
            | 'pending_transfer'
        }
      >({
        providesTags: (result) =>
          result
            ? [
                ...result?.map?.((x) => ({
                  type: 'Group/Members' as const,
                  id: x?.user?.id,
                })),
                { type: 'Group/Members', id: 'PARTIAL-LIST' },
              ]
            : [{ type: 'Group/Members', id: 'PARTIAL-LIST' }],
        query: ({ groupId, fromId, keyword, mode }) => ({
          url: `/v2/groups/${groupId}/members`,
          params: {
            ...(keyword ? { keyword: keyword } : {}),
            ...(fromId ? { from_id: fromId } : {}),
            ...(mode ? { mode } : {}),
            number: LOAD_NUMBER,
          },
        }),
        transformResponse: (response) => {
          return response?.group_users || []
        },
        keepUnusedDataFor: 0,
      }),

      getRelatableGroups: build.query({
        providesTags: ['Group/Relatable'],
        query: ({ groupId, keyword, from, number = LOAD_NUMBER }) => ({
          url: `/v1/groups/${groupId}/relatable`,
          params: {
            keyword,
            from,
            number,
          },
        }),
        transformResponse: (response) => {
          return {
            ...response,
            groups: response.groups.filter((g: any) => !g.is_related) || [],
          }
        },
      }),

      getRelatedGroups: build.query({
        providesTags: ['Group/Related'],
        query: ({ groupId, keyword, from, number = LOAD_NUMBER }) => ({
          url: `/v1/groups/${groupId}/related`,
          params: {
            keyword,
            from,
            number,
          },
        }),
      }),

      addRelatedGroups: build.mutation({
        invalidatesTags: ['Group/Related', 'Group/Relatable'],
        query: ({ groupId, relatedGroupIds }) => ({
          url: `/v1/groups/${groupId}/related`,
          method: 'PUT',
          data: qs.stringify(
            {
              related_group_id: relatedGroupIds,
            },
            {
              arrayFormat: 'bracket',
            }
          ),
        }),
      }),

      removeRelatedGroups: build.mutation({
        invalidatesTags: ['Group/Related', 'Group/Relatable'],
        query: ({ groupId, relatedGroupId }) => ({
          url: `/v1/groups/${groupId}/related`,
          method: 'DELETE',
          data: {
            related_group_id: relatedGroupId,
          },
        }),
      }),

      createGroup: build.mutation({
        invalidatesTags: ['Groups/Mine', 'UserGroups'],
        queryFn: async (data: GroupCreate, api) => {
          data.uuid = Cookie.deviceId.get()

          return await axiosBaseQuery({ client: 'self' })(
            {
              url: pathApiGroupsNew,
              method: 'POST',
              data,
            },
            api
          )
        },
      }),

      updateGroup: build.mutation({
        invalidatesTags: ['Group/Base'],
        queryFn: async (data: GroupCreate, api) => {
          data.uuid = getDeviceId()

          return await axiosBaseQuery({ client: 'self' })(
            {
              url: pathApiGroupsEdit({ groupId: data.id }),
              method: 'PUT',
              data,
            },
            api
          )
        },
      }),

      joinGroup: build.mutation<
        {
          group: Group
          status: 'success'
        },
        {
          groupId: string | number
        }
      >({
        invalidatesTags: (_result, error, _arg) =>
          error
            ? []
            : ['Group/Base', 'UserGroups', 'Groups/Mine', 'Group/Members'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/join`,
          method: 'POST',
        }),
      }),

      leaveGroup: build.mutation({
        invalidatesTags: [
          'Group/Base',
          'UserGroups',
          'Groups/Mine',
          'Group/Members',
          'Groups',
        ],
        query: (data) => ({
          url: `/v1/groups/${data.groupId}/leave`,
          method: 'DELETE',
          data,
        }),
      }),

      deleteGroup: build.mutation({
        invalidatesTags: ['Groups/Mine', 'UserGroups'],
        query: (data) => ({
          url: `/v1/groups/${data.groupId}/leave`,
          method: 'DELETE',
          data,
        }),
      }),

      deputize: build.mutation<
        { result: string },
        { groupId: string | number; user_ids: Array<string> }
      >({
        invalidatesTags: (_0, _1, args) => {
          return [
            ...args?.user_ids?.map?.((x) => {
              return { type: 'Group/Members' as const, id: x }
            }),
            { type: 'Group/Members', id: 'PARTIAL-LIST' },
            'Group/Base',
          ]
        },
        queryFn: async ({ groupId, user_ids }, api) => {
          return await axiosBaseQuery({
            client: 'self',
          })(
            {
              url: `/api/groups/${groupId}/deputize`,
              method: 'POST',
              data: {
                uuid: getDeviceId(),
                user_ids,
              },
            },
            api
          )
        },
      }),

      memberRequestAction: build.mutation<
        any,
        { groupId: string; userId: number; mode: 'accept' | 'decline' }
      >({
        invalidatesTags: ['Group/Base', 'Group/Members'],
        query: (data) => ({
          url: `/v1/groups/${data?.groupId}/${data?.mode}/${data?.userId}`,
          method: 'POST',
          data,
        }),
      }),

      takeOverGroup: build.mutation<any, any>({
        invalidatesTags: ['Group/Base'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/take_over`,
          method: 'POST',
        }),
      }),

      inviteToGroup: build.mutation<any, any>({
        query: ({ groupId, userIds }) => ({
          url: `/v1/groups/${groupId}/invite`,
          method: 'POST',
          data: {
            user_ids: userIds,
          },
        }),
      }),

      withdrawModeratorOffer: build.mutation<
        any,
        { groupId: string; userId: number }
      >({
        invalidatesTags: ['Group/Base', 'Group/Members'],
        query: ({ groupId, userId }) => ({
          url: `/v1/groups/${groupId}/deputize/${userId}/withdraw`,
          method: 'PUT',
        }),
      }),

      acceptModeratorOffer: build.mutation<any, any>({
        invalidatesTags: ['Group/Members', 'Activities', 'Group/Base'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/deputize`,
          method: 'PUT',
        }),
      }),

      rejectModeratorOffer: build.mutation<any, any>({
        invalidatesTags: ['Activities', 'Group/Base'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/deputize`,
          method: 'DELETE',
        }),
      }),

      removeModerator: build.mutation<
        any,
        {
          groupId: string
          userId: number
        }
      >({
        invalidatesTags: ['Group/Base', 'Group/Members'],
        query: ({ groupId, userId }) => ({
          url: pathApiGroupsFire({ groupId, userId }),
          method: 'POST',
        }),
      }),

      transferOwner: build.mutation<{}, { groupId: string; user_id: number }>({
        invalidatesTags: ['Group/Base', 'Group/Members'],
        queryFn: async ({ groupId, user_id }, api) => {
          return await axiosBaseQuery({
            client: 'self',
          })(
            {
              url: `/api/groups/${groupId}/transfer`,
              method: 'POST',
              data: {
                uuid: getDeviceId(),
                user_id,
              },
            },
            api
          )
        },
      }),

      withdrawOwnerTransfer: build.mutation<
        any,
        { groupId: string; user_id: number }
      >({
        invalidatesTags: ['Group/Base', 'Group/Members'],
        query: ({ groupId, user_id }) => ({
          url: `/v1/groups/${groupId}/transfer/withdraw`,
          method: 'PUT',
          data: {
            user_id,
          },
        }),
      }),

      acceptOwnerTransfer: build.mutation<any, any>({
        invalidatesTags: ['Group/Base', 'Group/Members', 'Activities'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/transfer`,
          method: 'PUT',
        }),
      }),

      rejectOwnerTransfer: build.mutation<any, any>({
        invalidatesTags: ['Activities', 'Group/Base'],
        query: ({ groupId }) => ({
          url: `/v1/groups/${groupId}/transfer`,
          method: 'DELETE',
        }),
      }),

      getBannedUsers: build.query<
        PaginationResult<User>,
        {
          groupId: string
          from: string
        }
      >({
        keepUnusedDataFor: 0,
        query: ({ groupId, from }) => ({
          url: `/v1/groups/${groupId}/ban_list`,
          params: {
            number: LOAD_NUMBER,
            ...(from == '0'
              ? {}
              : {
                  page: from,
                }),
          },
        }),
        transformResponse(
          baseQueryReturnValue: { users: User[] },
          _,
          { from }
        ) {
          const users = baseQueryReturnValue?.users ?? []
          let nextPage: number

          if (users.length === LOAD_NUMBER) {
            nextPage = Number(from) + 1
          }

          return {
            list: users,
            next_page_value: nextPage,
          }
        },
      }),

      banUser: build.mutation({
        query: ({ groupId, userId }) => ({
          url: `/v1/groups/${groupId}/ban/${userId}`,
          method: 'POST',
        }),
      }),

      unBanUser: build.mutation({
        query: ({ groupId, userId }) => ({
          url: `/v1/groups/${groupId}/unban/${userId}`,
          method: 'POST',
        }),
      }),

      groupsCallList: build.query<
        {
          next_page_value: number
          result: 'success'
          posts: Array<Post>
        },
        {
          scope: 'all' | 'joined'
          groupCategoryId?: string
          fromTimeStamp?: string
        }
      >({
        query: ({ scope = 'all', groupCategoryId, fromTimeStamp }) => ({
          url: '/v1/posts/group_calls',
          params: omitBy(
            {
              number: LOAD_NUMBER,
              scope,
              group_category_id: groupCategoryId,
              ...(fromTimeStamp == '0'
                ? {}
                : {
                    from_timestamp: fromTimeStamp,
                  }),
            },
            isNil
          ),
        }),
        keepUnusedDataFor: 0,
      }),

      requestWalkthrough: build.mutation<any, { groupId: string }>({
        query: ({ groupId }) => ({
          url: pathApiGroupsShowRequestWalkthrough({ groupId }),
          method: 'POST',
        }),
        invalidatesTags: ['Group/Base'],
      }),

      getGroupsJoinedStatuses: build.query<
        {
          [key: string]: GroupRelationship
        },
        { ids: Array<number> }
      >({
        providesTags: ['Group/JoinedStatuses'],
        query: ({ ids }) => ({
          url: pathApiGroupsJoinedStatuses,
          params: {
            ids,
          },
        }),
        keepUnusedDataFor: 0,
      }),

      getMutedUsers: build.query<
        PaginationResult<User>,
        { groupId: string; from: string }
      >({
        keepUnusedDataFor: 0,
        query: ({ groupId, from }) => ({
          url: `/v1/group_mute/${groupId}/muted_users`,
          params: {
            number: LOAD_NUMBER,
            page: from == '0' ? undefined : from,
          },
        }),
        transformResponse(
          baseQueryReturnValue: { group_users: { user: User }[] },
          _,
          { from }
        ) {
          const users = baseQueryReturnValue?.group_users ?? []
          let nextPage: number

          if (users.length === LOAD_NUMBER) {
            nextPage = Number(from) + 1
          }

          return {
            list: users.map((x) => ({ ...x.user, isMuted: true })),
            next_page_value: nextPage,
          }
        },
      }),

      muteUser: build.mutation({
        query: ({ groupId, userId }) => ({
          url: `/v1/group_mute/${groupId}/mute/${userId}`,
          method: 'POST',
        }),
      }),

      unMuteUser: build.mutation({
        query: ({ groupId, userId }) => ({
          url: `/v1/group_mute/${groupId}/unmute/${userId}`,
          method: 'DELETE',
        }),
      }),
    }),
    overrideExisting: false,
  })

export const {
  endpoints,
  useGetGroupCategoriesQuery,
  useGetMineQuery,
  useGetMinePinnedQuery,
  useGetUserGroupListQuery,
  useGetGroupsQuery,
  useGetGroupQuery,
  useGetGroupMutateMutation,
  useVisitGroupMutation,
  useGetMembersQuery,
  useGetRelatableGroupsQuery,
  useGetRelatedGroupsQuery,
  useAddRelatedGroupsMutation,
  useRemoveRelatedGroupsMutation,
  useUpdateGroupMutation,
  useCreateGroupMutation,
  useJoinGroupMutation,
  useLeaveGroupMutation,
  useDeleteGroupMutation,
  useDeputizeMutation,
  useMemberRequestActionMutation,
  useTakeOverGroupMutation,
  useInviteToGroupMutation,
  useWithdrawModeratorOfferMutation,
  useAcceptModeratorOfferMutation,
  useRejectModeratorOfferMutation,
  useRemoveModeratorMutation,
  useTransferOwnerMutation,
  useWithdrawOwnerTransferMutation,
  useAcceptOwnerTransferMutation,
  useRejectOwnerTransferMutation,
  useGetBannedUsersQuery,
  useBanUserMutation,
  useUnBanUserMutation,
  useGroupsCallListQuery,
  useRequestWalkthroughMutation,
  useGetGroupsJoinedStatusesQuery,
  useGetMutedUsersQuery,
  useMuteUserMutation,
  useUnMuteUserMutation,
} = groupApi
